// EvaluationSummary.js


import React from "react";
import { useState, useEffect, useContext, useRef } from "react";
import { useFetch } from "use-http";
import { useNavigate } from "react-router-dom";
import { Box, Button, Text, Spinner, Layer, ResponsiveContext } from "grommet";
import { getDBURL, convertDateToDisplayformat, pluck } from "../Utils/Utils";
import { getLabelByIndex, getOptionsByName} from "../Utils/RBGUtils";
import useTrapFocus from "../Utils/useTrapFocus";
import RangeWithText from "../Base components/RangeWithText";
import { values } from "underscore";
import StationList from "./StationList";
import { useErrorLogger } from './Logger';
import { UserContext } from "../../App";


const EvaluationSummary = (props) => {
    const {plant, evaluation, station, treatment, mainColor} = props;
    const currentUser = useContext(UserContext);
    const { DBlogError } = useErrorLogger();
    // const navigate = useNavigate();
    // console.log("In EvaluationSummary props >>  ", props);

    // console.log("plant >>  ", plant);
    // console.log("evaluation in summary >>  ", evaluation);

    const [loading, setLoading] = useState(true);

    const { get } = useFetch( getDBURL() );  

    const [preHarvestValues, setPreHarvestValues] = useState({});

    // Ref for the modal and focus trap to keep focus within it  
    const modalRef = useRef(null);
    useTrapFocus(modalRef, true); // Trap focus inside the modal while it's open

    useEffect(() => {
        if (evaluation) { // Ensure evaluation is not undefined or falsy
            (async function loadPreHarvestHandler() {
                try {
                    const response = await get(`/loadPreHarvestValues/preharvest_ID=${evaluation}/dt=${Date.now()}`);
                    // console.log("loadPreHarvestHandler result:", response);
                    
                    // Check if the response is successful
                    if (!response.success) {
                        throw new Error(`HTTP error! ${response.message}`);
                    }
                    
                    const data = response.result;                    
                    // console.log("loadPreHarvestHandler result:", data);
                    
                    if (data !== undefined && data.length > 0) {
                        
                        setPreHarvestValues({
                            PreHarvestID: data[0].Id,
                            SeedlingID: data[0].SeedlingId,   
                            TreatmentId: data[0].TreatmentId,
                            StationId: data[0].StationId,
                            Year: data[0].Year,
                            Grade: data[0].Grade, 
                            CreateDate: data[0].Create_Date,            
                            StatusID: data[0].StatusID, 
                            TreatmentPlan: data[0].TreatmentPlan,                       
                            Pruning: data[0].Pruning,
                            Fertility: data[0].Fertility,
                            Pathology: data[0].Pathology,
                            Rotten: data[0].Rotten,
                            ColorUniformity: data[0].Color_Uniformity,
                            ShotBerries: data[0].Shot_Berries,
                            WaterBerries: data[0].Water_Berries,
                            BunchDensity: data[0].Bunch_Density,
                            BunchShapeSC: data[0].Short_Conical,
                            BunchShapeCS: data[0].Conical_Shouldered,
                            BunchShapeLC: data[0].Long_Conical,
                            BunchShapeC: data[0].Cylindrical_Bunch,
                            BunchShapeWC: data[0].Winged_Cylindrical,
                            BunchShapeWD: data[0].Winged_Double,
                            Rudiment: data[0].Rudiment,
                            Seeded: data[0].Seeded,
                            OverallAppeal: data[0].Overall_Appeal,
                            BerryTexture: data[0].Berry_texture,
                            Skin: data[0].Skin,
                            TSSAtEval: data[0].TSS_At_Eval,
                            TSSAtRipeness: data[0].TSS_At_Ripeness,
                            PHAtRipeness: data[0].PH_At_Ripeness,
                            TAAtRipeness: data[0].TA_At_Ripeness,
                            // AvgBerryWeight: data[0].Avg_Berry_Weight ?? 0,
                            AvgBerryWeight: data[0].Avg_Berry_Weight,
                            AvgBerryDiameter: data[0].Avg_Berry_Diameter,
                            AvgBerryArea: data[0].Avg_Berry_Area,
                            AvgBerryFirmness: data[0].Avg_Berry_Firmness,
                            Stem: data[0].Stem,
                            BerrySize: data[0].Berry_Size_Uniformity,
                            AcidAtEval: data[0].Acid_At_Eval,
                            Attachment: data[0].Attachment,
                            Astringency: data[0].Astringency,
                            Flavor: data[0].Flavor,
                            EatingQuality: data[0].Eating_Quality,
                            SkinStructure: data[0].Skin_Structure,
                            CapStem: data[0].Cap_Stem,
                            Brush: data[0].Brush,
                            BerryShapeC: data[0].Cylindrical_Berry,  
                            BerryShapeE: data[0].Ellipsoid,
                            BerryShapeO: data[0].Obovoid,
                            BerryShapeOv: data[0].Ovoid,
                            BerryShapeR: data[0].Round,              
                            Notes: data[0].Notes,
                            BunchImgExist: data[0].BunchImgExist,
                            BerryImgExist: data[0].BerryImgExist,
                            HalvedBerryImgExist: data[0].HalvedBerryImgExist,
                            FreeImgText: data[0].FreeImgText,
                            FreeImgExist: data[0].FreeImgExist,
                            EvaluationProgress: data[0].EvaluationProgress ?? 0,
                        });
                        setLoading(false);
                    }
                } catch (error) {
                    console.log("Error loading pre-harvest data:", error);

                    await DBlogError({
                        error,
                        parameters: JSON.stringify({ evaluation: evaluation }),
                        userName: currentUser.email,
                        moduleName: 'EvaluationSummary',
                        functionName: 'loadPreHarvestHandler',
                    });

                }
            })();
        }
    }, [evaluation]); // Add evaluation as a dependency    
    // console.log('preHarvestValues:', preHarvestValues);
    
    const getShapeValue = (item) => {
        if (item.type !== "Shape")
            return ""; 

        let value = "";
        let shapesList = [];    
        item.items.map(option => {  
            if(preHarvestValues[option.fieldName] === true) {
                shapesList = [...shapesList, option.label];
            }            
        })

        if (shapesList.length > 0) {
          value = shapesList.reduce((acc, curr) => {
            return acc + (acc !== '' ? ',\n ' : '') + curr;
          }, '');
        }
        
        // console.log(value);                
        return value;
    };

    const FieldTitleMap = [
        {name: 'Pruning', fieldName: 'Pruning', title:'Pruning', type: "Radio"},
        {name: 'Fertility', fieldName: 'Fertility', title:'Fertility', type: "Radio"},
        {name: 'Pathology', fieldName: 'Pathology', title:'Pathology', type: "Radio"},
        {name: 'Rotten', fieldName: 'Rotten', title: 'Rotten', type: "Bool"},
        {name: 'ColorUniformity', fieldName: 'ColorUniformity', title:'Color Uniformity', type: "Radio"},
        {name: 'BerrySize', fieldName: 'BerrySize', title: 'Size Uniformity', type: "Radio"},
        {name: 'ShotBerries', fieldName: 'ShotBerries', title: 'Shot Berries', type: "Radio"},
        {name: 'WaterBerries', fieldName: 'WaterBerries', title:'Water Berries', type: "Radio"},
        {name: 'BunchDensity', fieldName: 'BunchDensity', title:'Bunch Density', type: "Radio"},
        {name: 'BunchShape', title:'Bunch Shape', type: "Shape",
            items: [{label: "Conical Shouldered", fieldName: "BunchShapeCS"},
                {label: "Cylindrical", fieldName: "BunchShapeC"},
                {label: "Long Conical", fieldName: "BunchShapeLC"},
                {label: "Short Conical", fieldName: "BunchShapeSC"},
                {label: "Winged Cylindrical", fieldName: "BunchShapeWC"},
                {label: "Winged Double", fieldName: "BunchShapeWD"}]
        },
        {name: 'BerryShape', title:'Berry Shape', type: "Shape",
            items: [{label: "Cylindrical", fieldName: "BerryShapeC"},
                {label: "Ellipsoid", fieldName: "BerryShapeE"},
                {label: "Obovoid", fieldName: "BerryShapeO"},
                {label: "Ovoid", fieldName: "BerryShapeOv"},
                {label: "Round", fieldName: "BerryShapeR"}] },
        {name: 'SkinStructure', fieldName: 'SkinStructure', title: 'Skin Structure', type: "Radio"},
        {name: 'Stem', fieldName: 'Stem', title: 'Stem', type: "Radio"},
        {name: 'CapStem', fieldName: 'CapStem', title: 'Cap Stem', type: "Radio"},
        {name: 'OverallAppeal', fieldName: 'OverallAppeal', title: 'Overall Appeal', type: "Radio"},
        {name: 'Attachment', fieldName: 'Attachment', title: 'Attachment', type: "Radio"},
        {name: 'Brush', fieldName: 'Brush', title: 'Brush', type: "Radio"},
        {name: 'Seeded', fieldName: 'Seeded', title: 'Seeded', type: "Bool"},
        {name: 'Rudiment', fieldName: 'Rudiment', title: 'Rudiment', type: "Radio"},
        {name: 'BerryTexture', fieldName: 'BerryTexture', title: 'Berry Texture', type: "Radio"},
        {name: 'AcidAtEval', fieldName: 'AcidAtEval', title: 'Acidity at Evaluation', type: "Radio"},
        {name: 'Flavor', fieldName: 'Flavor', title: 'Flavor', type: "Radio"},
        {name: 'Astringency', fieldName: 'Astringency', title: 'Astringency', type: "Radio"},
        {name: 'Skin', fieldName: 'Skin', title: 'Skin', type: "Radio"},
        {name: 'EatingQuality', fieldName: 'EatingQuality', title: 'Eating Quality', type: "Radio"},

    
        // Conditionally add the items to end
        ...(plant === null ? [
            { name: 'TSSAtRipeness', fieldName: 'TSSAtRipeness', title: 'TSS at Ripeness', type: "Text" },
            { name: 'PHAtRipeness', fieldName: 'PHAtRipeness', title: 'pH at Ripeness', type: "Text" },
            { name: 'TAAtRipeness', fieldName: 'TAAtRipeness', title: 'TA at Ripeness', type: "Text" },
            { name: 'AvgBerryWeight', fieldName: 'AvgBerryWeight', title: 'Avg Berry Weight', type: "Text" },
            { name: 'AvgBerryDiameter', fieldName: 'AvgBerryDiameter', title: 'Avg Berry Diameter', type: "Text" },
            { name: 'AvgBerryArea', fieldName: 'AvgBerryArea', title: 'Avg Berry Area', type: "Text" },
            { name: 'AvgBerryFirmness', fieldName: 'AvgBerryFirmness', title: 'Firmness', type: "Text" }
        ] : []),
    ];

    // Conditionally add items after Seeded based on `plant === null`
    if (plant === null) {
        const additionalFields = [
            {name: 'TSSAtEval', fieldName: 'TSSAtEval', title: 'TSS at Eval', type: "Text"},
        ];
    
        // Insert the additional items right after `Brush` and before `Seeded`
        const indexOfBrush = FieldTitleMap.findIndex(item => item.fieldName === 'Brush');
        FieldTitleMap.splice(indexOfBrush + 1, 0, ...additionalFields);
    };
    

    /** DrawTable creates a table layout by grouping items from FieldTitleMap into pairs. 
     *  If there is an odd number of items, the last item is paired with null. 
     *  It returns an array of row components.
    */
    const DrawTable = () => {
        return FieldTitleMap.reduce((table, item, i) => {
            if (i % 2 === 0) {
                let second = FieldTitleMap[i + 1] || null;
                table.push(DrawRow([item, second], i));
            }
            return table;
        }, []);
    };

    const DrawRow = (arr, index) => {
        return (
            <Box key={`row-${index}`} direction="row-responsive" align="center" justify="between" margin="xsmall">
                <Box width="100%" direction="row-responsive" height="100%" pad="xxsmall">
                    {arr.filter(item => item !== null).map((item, idx) => DrawItem(item, `item-${index}-${idx}`))}
                </Box>                                                                                 
            </Box>
        );
    };

    // console.log('preHarvestValues  ', preHarvestValues);
    const DrawItem = (item) =>
    {
        // console.log('In DrawItem prpertyName >> ', item.name);
        // console.log('In DrawItem propertyValue >> ', preHarvestValues[item.fieldName]); 
        // console.log('In DrawItem item >> ', item);

        const value = item.type === "Radio" ?  getLabelByIndex(item.name, preHarvestValues[item.fieldName]) 
        : item.type === "Bool" && preHarvestValues[item.fieldName] ? "Yes" 
        : item.type === "Bool" && !preHarvestValues[item.fieldName] ? "No"
        : item.type === "Shape" ? getShapeValue(item)
        : preHarvestValues[item.fieldName];

        //const rangeData = item.type === "Radio" ? createArrOptions(item.name) : null;
        
        return(
            <Box  key={item.name} width="100%" direction="row-responsive" style={{paddingTop: "5px"}}>
                <Box  width="90%"  direction="row" align="center"
                    {...(item.type === "Radio" && { justify: "between" })}
                 >
                    <Text  weight="bold" style={{fontStyle: "italic", whiteSpace: "nowrap"}}>&nbsp;{item.title !== undefined ? item.title+':' : ''}&ensp;</Text>
                    {/* <Text style={{fontStyle: "italic", whiteSpace: "nowrap", lineBreak: "auto"}} >{value}</Text>            */}
                    {item.type === "Radio" ? 
                    drawRangeComponent(item)
                    : <Text style={{fontStyle: "italic", lineBreak: "strict"}} >{value}</Text>         
                    }
                    {/* <Text style={{fontStyle: "italic"}} ><span style={{whiteSpace: 'pre-line'}}>{value}</span></Text> */}
                </Box>  
            </Box> 
        );
    };

    const drawRangeComponent = (item) => {
        // console.log('In drawRangeComponent item: >> ', item)
        return (
            <>
             {(item.type === "Radio" && preHarvestValues[item.fieldName] !== null)
             ? createArrOptions(item)
             :  <></>
             }   
            </>
        );
    };
    
    const screenSize = useContext(ResponsiveContext);
    // console.log('screenSize in summary  >> ', screenSize);
    const actualLabelOnly = screenSize === "xsmall" || screenSize === "small" || screenSize === "medium";

    const createArrOptions = (item) => {
        const getOptions = getOptionsByName(item.name);

        if (getOptions !== null) {
            const optionsArr = getOptions();
            // console.log('optionsArr >> ', optionsArr);
    
            const isGradeEqual = optionsArr.every(option => option.grade === optionsArr[0].grade);

            const options = optionsArr.map(option => { 
                return {
                    value: option.index,
                    range: isGradeEqual ? '' : option.grade,
                    label: option.label
                };
            });
            
            // console.log('options >> ', options);
            // console.log('pointColor >> ', mainColor ? `${mainColor}` :  "#00739D");

            return (

                <RangeWithText
                    rangeOptions = {options}
                    actualValue={preHarvestValues[item.fieldName]}
                    actualLabel={getLabelByIndex(item.name, preHarvestValues[item.fieldName]) }
                    actualLabelOnly={actualLabelOnly}
                    transparent = {true}
                    pointColor = {mainColor ? `${mainColor}` :  "#00739D"}
                />

            );
        }        
        return null;
    };
      

    const style = {
        border: "2px solid",
        margin: "10px",
    };

    return (
        <Layer
            id="eval-summ"
            ref={modalRef}
            tabIndex="-1"
            position="center"
            onEsc={() =>  props.setShowEvaluationSummary(false)}
            onClickOutside={() =>{}}
        >
            {loading ? (
            <Box align="center" justify="around" direction="row" width="1000px" height="600px" gap="small" pad="small">
                <Spinner  size="xxlarge"/> <Text>Loading...</Text>
            </Box>
             ) : 
             (
            <Box width="1000px" height="100%" style={style}  pad="small"  background="light-3"  >
                {/* data about seedling */}
                {plant !== null &&
                <Box pad={{vertical: "small"}} align="center">

                    <Box pad={{horizontal: "xsmall"}} direction="row-responsive">
                        <Box direction="row">
                            <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium" > {plant.IsSeedling === 1 ? "SEEDLING:" : "SCION:"} </Text>
                        </Box>                        
                        <Box pad={{horizontal: "xsmall"}} direction="row">
                            {/* <Box direction="row">
                                <Text margin={{horizontal: "xsmall"}} size="medium" > id:</Text>
                                <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium" > {plant.SID} </Text>
                            </Box> */}
                            <Box direction="row" >
                                <Text margin={{horizontal: "xsmall"}} size="medium"> year: </Text>  
                                <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium"> {plant.Year} </Text> 
                            </Box>
                        </Box>
                        <Box pad={{horizontal: "xsmall"}} direction="row">
                            <Box direction="row">
                                <Text margin={{horizontal: "xsmall"}} size="medium" > row:</Text>
                                <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium" > {plant.Row} </Text>
                            </Box>                    
                            <Box direction="row" >
                                <Text margin={{horizontal: "xsmall"}} size="medium"> vine:</Text>    
                                <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium"> {plant.Column} </Text> 
                            </Box>
                        </Box>
                    </Box>
                </Box>
                }
                {/* end of data about seedling */}

                {/* data about station */}
                {station !== null &&
                <Box pad={{vertical: "small"}} align="center">

                    <Box pad={{horizontal: "xsmall"}} direction="row-responsive">
                        <Box direction="row"  align="end" >
                            <Text weight="bold" margin={{horizontal: "xsmall"}} size="medium" > STATION: </Text>
                            <Text weight="bold" style={{fontStyle: "italic"}} margin={{horizontal: "xsmall"}} size="large"> {station.Name} </Text> 
                        </Box>                        
                    </Box>
                </Box>
                }
                {/* end of data about station */}                

                {/* start table */}
                <Box width='97%' 
                    margin="small" 
                    pad="small"
                    height="600px"
                    border>

                    <Box  width="250px" pad="medium">
                        <Text weight="bold"
                            style={{
                                background: `linear-gradient(to left, ${mainColor ? mainColor : "#00739D"}, #f9dd94 100%)`,
                                backgroundPosition: "0 100%",
                                backgroundSize: "100% 2px",
                                backgroundRepeat: "repeat-x",
                                paddingBottom: "10px",
                                fontStyle: "italic"}}
                            >
                            Evaluation {convertDateToDisplayformat(preHarvestValues.CreateDate)}</Text>                   
                    </Box>
                    <Box flex={true} align='center' background='light-1' overflow='auto'  >
                        <Box flex={false} 
                            // background={{ size: "60%", repeat: "no-repeat", opacity: "10%", image: "url('Grape.png')" }}
                            background={{
                                image: "url('/Magnifier.jpg')",  // Adjusting transparency of the gradient layer
                                size: "contain",
                                repeat: "no-repeat",
                                opacity: 0.2,  // Opacity for the combined image and gradient
                            }}
                            height="auto" style={{ width: '900px', height: 'auto' }}>
                            {(preHarvestValues !== null) && DrawTable()}
                         </Box>
                    </Box>

                </Box>
                {/* end table */}
                <Box  pad="xsmall" align="center" justify="between" >
                        <Button label="close" 
                            style={{color: "#ffffff"}}
                            primary onClick={() => props.setShowEvaluationSummary(false)} 
                        />   
                </Box>  
            </Box>
            )}
        </Layer>
    );
};
export default EvaluationSummary;
